import * as React from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { cn } from "@/lib/utils"

/**
 * DataPagination - A reusable pagination component for list views
 * 
 * @param {number} currentPage - Current page number (1-indexed)
 * @param {number} totalItems - Total number of items
 * @param {number} pageSize - Number of items per page
 * @param {function} onPageChange - Callback when page changes (receives page number)
 * @param {function} onPageSizeChange - Callback when page size changes (receives new size)
 * @param {number[]} pageSizeOptions - Array of page size options
 * @param {boolean} showPageSizeSelector - Whether to show page size selector
 * @param {boolean} showTotalCount - Whether to show total count info
 * @param {string} className - Additional CSS classes
 */
export function DataPagination({
  currentPage = 1,
  totalItems = 0,
  pageSize = 10,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 20, 50, 100],
  showPageSizeSelector = true,
  showTotalCount = true,
  className,
}) {
  const totalPages = Math.ceil(totalItems / pageSize)
  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1
  const endItem = Math.min(currentPage * pageSize, totalItems)

  const canGoPrevious = currentPage > 1
  const canGoNext = currentPage < totalPages

  const handlePrevious = () => {
    if (canGoPrevious) {
      onPageChange?.(currentPage - 1)
    }
  }

  const handleNext = () => {
    if (canGoNext) {
      onPageChange?.(currentPage + 1)
    }
  }

  const handlePageSizeChange = (newSize) => {
    onPageSizeChange?.(parseInt(newSize, 10))
    // Reset to first page when page size changes
    onPageChange?.(1)
  }

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages = []
    const maxVisiblePages = 5
    
    if (totalPages <= maxVisiblePages) {
      // Show all pages if total is small
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i)
      }
    } else {
      // Show first, last, current, and adjacent pages
      const startPages = [1]
      const endPages = [totalPages]
      const middlePages = []
      
      // Calculate middle range
      let start = Math.max(2, currentPage - 1)
      let end = Math.min(totalPages - 1, currentPage + 1)
      
      // Adjust if we're near the start
      if (currentPage <= 3) {
        end = Math.min(4, totalPages - 1)
      }
      
      // Adjust if we're near the end
      if (currentPage >= totalPages - 2) {
        start = Math.max(2, totalPages - 3)
      }
      
      for (let i = start; i <= end; i++) {
        middlePages.push(i)
      }
      
      // Build final array with ellipsis markers
      pages.push(...startPages)
      
      if (middlePages.length > 0 && middlePages[0] > 2) {
        pages.push('ellipsis-start')
      }
      
      pages.push(...middlePages)
      
      if (middlePages.length > 0 && middlePages[middlePages.length - 1] < totalPages - 1) {
        pages.push('ellipsis-end')
      }
      
      if (totalPages > 1) {
        pages.push(...endPages)
      }
    }
    
    return pages
  }

  if (totalItems === 0) {
    return null
  }

  return (
    <div
      className={cn(
        "flex flex-col sm:flex-row items-center justify-between gap-4 py-4",
        className
      )}
      data-testid="data-pagination"
    >
      {/* Left side: Item count info */}
      <div className="flex items-center gap-4 text-sm text-muted-foreground">
        {showTotalCount && (
          <span data-testid="pagination-info">
            Showing {startItem} to {endItem} of {totalItems} results
          </span>
        )}
        
        {showPageSizeSelector && (
          <div className="flex items-center gap-2">
            <span>Show</span>
            <Select
              value={pageSize.toString()}
              onValueChange={handlePageSizeChange}
            >
              <SelectTrigger className="h-8 w-[70px]" data-testid="page-size-select">
                <SelectValue placeholder={pageSize} />
              </SelectTrigger>
              <SelectContent>
                {pageSizeOptions.map((size) => (
                  <SelectItem key={size} value={size.toString()}>
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span>per page</span>
          </div>
        )}
      </div>

      {/* Right side: Page navigation */}
      <nav
        role="navigation"
        aria-label="Pagination"
        className="flex items-center gap-1"
      >
        {/* Previous button */}
        <Button
          variant="outline"
          size="sm"
          onClick={handlePrevious}
          disabled={!canGoPrevious}
          className="gap-1"
          data-testid="pagination-previous"
        >
          <ChevronLeft className="h-4 w-4" />
          <span className="hidden sm:inline">Previous</span>
        </Button>

        {/* Page numbers */}
        <div className="flex items-center gap-1">
          {getPageNumbers().map((page, index) => {
            if (page === 'ellipsis-start' || page === 'ellipsis-end') {
              return (
                <span
                  key={page}
                  className="flex h-8 w-8 items-center justify-center text-muted-foreground"
                >
                  ...
                </span>
              )
            }
            
            return (
              <Button
                key={page}
                variant={currentPage === page ? "default" : "outline"}
                size="sm"
                className="h-8 w-8 p-0"
                onClick={() => onPageChange?.(page)}
                data-testid={`pagination-page-${page}`}
              >
                {page}
              </Button>
            )
          })}
        </div>

        {/* Next button */}
        <Button
          variant="outline"
          size="sm"
          onClick={handleNext}
          disabled={!canGoNext}
          className="gap-1"
          data-testid="pagination-next"
        >
          <span className="hidden sm:inline">Next</span>
          <ChevronRight className="h-4 w-4" />
        </Button>
      </nav>
    </div>
  )
}

export default DataPagination
